/**
 * AdminNewsletterPage — Admin-Newsletter mit Segment-Filtern (heliumtracker-web#96)
 *
 * Ein Formular fuer beide Faelle (Anlegen und Bearbeiten): Betreff, HTML-Body,
 * 8 Segment-Checkboxen, Ausschluss-Newsletter, Empfaenger-Obergrenze, mit
 * Live-Empfaengerzahl (POST .../preview_count, debounced). Dazu die Liste
 * bisheriger Newsletter mit Bearbeiten, Vorschau, Testmail und Versand.
 * Nur fuer Admins sichtbar — Muster wie AdminUsersPage.tsx (siehe dort fuer
 * die Begruendung des Guards).
 *
 * Die Vorschau rendert das fertige Mail-HTML in ein sandboxed iframe (srcDoc,
 * kein allow-scripts, kein allow-same-origin): der Body ist beliebiges HTML
 * aus dem Textfeld und hat im Admin-Dokument nichts verloren.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useUserPermissions } from '../../hooks/useUserPermissions'
import { toast } from '../../components/Toast/useToast'
import {
  NEWSLETTER_SEGMENTS,
  useAdminNewsletter,
  useAdminNewsletters,
  useCreateNewsletter,
  useNewsletterPreview,
  useNewsletterPreviewCount,
  usePublishNewsletter,
  useTestSendNewsletter,
  useUpdateNewsletter,
  type AdminNewsletter,
} from './useAdminNewsletters'

const PREVIEW_DEBOUNCE_MS = 400

function formatDate(value: string | null, locale: string): string {
  if (!value) return ''
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

export function AdminNewsletterPage() {
  const { t, i18n: i18next } = useTranslation('adminNewsletters')
  const { isAdmin, isLoaded } = useUserPermissions()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoaded && !isAdmin) navigate({ to: '/dashboard', replace: true })
  }, [isLoaded, isAdmin, navigate])

  const [editingId, setEditingId] = useState<number | null>(null)
  const [previewId, setPreviewId] = useState<number | null>(null)

  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [segments, setSegments] = useState<string[]>([])
  const [excludeId, setExcludeId] = useState<number | ''>('')
  const [maxRecipients, setMaxRecipients] = useState<number | ''>('')

  // Detail (inkl. body_html) wird nur geladen, wenn wirklich bearbeitet wird.
  const editing = useAdminNewsletter(editingId)
  // Genau EINMAL pro Newsletter ins Formular uebernehmen. Ohne diese Marke
  // wuerde jeder Hintergrund-Refetch (react-query holt bei Fensterfokus neu)
  // einen halbfertigen Entwurf im Textfeld ueberschreiben.
  const filledFromId = useRef<number | null>(null)
  useEffect(() => {
    const loaded = editing.newsletter
    if (!loaded || loaded.id !== editingId) return
    if (filledFromId.current === editingId) return
    filledFromId.current = editingId
    setSubject(loaded.subject)
    setBodyHtml(loaded.body_html ?? '')
    setSegments([...loaded.segments])
    setExcludeId(loaded.exclude_newsletter_id ?? '')
    setMaxRecipients(loaded.max_recipients ?? '')
  }, [editing.newsletter, editingId])

  // Debounced Preview-Params (400ms) — vermeidet einen Request pro Tastendruck/Klick,
  // Muster wie der Such-Debounce in AdminUsersPage.tsx.
  const [previewParams, setPreviewParams] = useState({
    segments: [] as string[],
    exclude_newsletter_id: null as number | null,
    max_recipients: null as number | null,
  })
  useEffect(() => {
    const timer = setTimeout(() => {
      setPreviewParams({
        segments,
        exclude_newsletter_id: excludeId === '' ? null : excludeId,
        max_recipients: maxRecipients === '' ? null : maxRecipients,
      })
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [segments, excludeId, maxRecipients])

  const preview = useNewsletterPreviewCount(previewParams)
  const { newsletters, isLoading, isError, refetch } = useAdminNewsletters()
  const createNewsletter = useCreateNewsletter()
  const updateNewsletter = useUpdateNewsletter()
  const publishNewsletter = usePublishNewsletter()
  const testSend = useTestSendNewsletter()
  const mailPreview = useNewsletterPreview(previewId)
  const [publishError, setPublishError] = useState<{ id: number; message: string } | null>(null)

  function resetForm() {
    filledFromId.current = null
    setEditingId(null)
    setSubject('')
    setBodyHtml('')
    setSegments([])
    setExcludeId('')
    setMaxRecipients('')
  }

  function toggleSegment(segment: string) {
    setSegments((prev) => (prev.includes(segment) ? prev.filter((s) => s !== segment) : [...prev, segment]))
  }

  function formValues() {
    return {
      subject,
      body_html: bodyHtml,
      segments,
      exclude_newsletter_id: excludeId === '' ? null : excludeId,
      max_recipients: maxRecipients === '' ? null : maxRecipients,
    }
  }

  function handleCreate() {
    createNewsletter.mutate(formValues(), {
      onSuccess: () => {
        toast.success(t('toast.createSuccess'))
        resetForm()
      },
      onError: (err: Error) => toast.error(err.message),
    })
  }

  function handleUpdate() {
    if (editingId === null) return
    updateNewsletter.mutate(
      { id: editingId, ...formValues() },
      {
        onSuccess: () => {
          toast.success(t('toast.updateSuccess'))
          resetForm()
        },
        onError: (err: Error) => toast.error(err.message),
      },
    )
  }

  function handleEdit(newsletter: AdminNewsletter) {
    filledFromId.current = null
    setEditingId(newsletter.id)
    // Betreff sofort aus der Liste, damit das Formular nicht leer aufblitzt,
    // bis das Detail (mit dem Body) da ist.
    setSubject(newsletter.subject)
    setBodyHtml('')
    setSegments([...newsletter.segments])
    setExcludeId(newsletter.exclude_newsletter_id ?? '')
    setMaxRecipients(newsletter.max_recipients ?? '')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function handleTestSend(newsletter: AdminNewsletter) {
    testSend.mutate(newsletter.id, {
      onSuccess: (result) => toast.success(t('toast.testSendSuccess', { email: result.sent_to })),
      onError: (err: Error) => toast.error(err.message),
    })
  }

  function handlePublish(newsletter: AdminNewsletter) {
    const recipients = newsletter.recipient_count
    if (!window.confirm(t('list.publishConfirm', { count: recipients }))) return
    setPublishError(null)
    publishNewsletter.mutate(newsletter.id, {
      onSuccess: () => toast.success(t('toast.publishSuccess')),
      onError: (err: Error & { status?: number }) => {
        if (err.status === 409) {
          setPublishError({ id: newsletter.id, message: err.message })
        } else {
          toast.error(err.message)
        }
      },
    })
  }

  const publishedNewsletters = (newsletters ?? []).filter((n) => n.published_at)
  const isEditing = editingId !== null
  const isSaving = createNewsletter.isPending || updateNewsletter.isPending
  const canSubmit = subject.trim().length > 0 && bodyHtml.trim().length > 0 && segments.length > 0 && !isSaving

  return (
    <div className="admin-newsletter-page" data-testid="admin-newsletter-page">
      <div className="page-header">
        <div>
          <h1>{t('page.title')}</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            {t('page.subtitle')}
          </p>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 24 }} data-testid="admin-newsletter-form">
        <h2 style={{ marginTop: 0, fontSize: 18 }} data-testid="newsletter-form-title">
          {isEditing ? t('form.editTitle', { subject: editing.newsletter?.subject ?? subject }) : t('form.createTitle')}
        </h2>

        {isEditing && editing.isLoading && (
          <p className="muted" data-testid="newsletter-detail-loading">
            {t('form.detailLoading')}
          </p>
        )}

        <div style={{ display: 'grid', gap: 12 }}>
          <label>
            <div className="muted" style={{ marginBottom: 4 }}>{t('form.subjectLabel')}</div>
            <input
              className="input"
              style={{ width: '100%' }}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="newsletter-subject"
            />
          </label>

          <label>
            <div className="muted" style={{ marginBottom: 4 }}>{t('form.bodyLabel')}</div>
            <textarea
              className="input"
              style={{ width: '100%', minHeight: 160, fontFamily: 'monospace' }}
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              data-testid="newsletter-body"
            />
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>{t('form.bodyHint')}</div>
          </label>

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }} data-testid="newsletter-segments">
            <legend className="muted" style={{ marginBottom: 4 }}>{t('form.segmentsLabel')}</legend>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
              {NEWSLETTER_SEGMENTS.map((segment) => (
                <label key={segment} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={segments.includes(segment)}
                    onChange={() => toggleSegment(segment)}
                    data-testid={'newsletter-segment-' + segment}
                  />
                  {t('segments.' + segment)}
                </label>
              ))}
            </div>
          </fieldset>

          <label>
            <div className="muted" style={{ marginBottom: 4 }}>{t('form.excludeLabel')}</div>
            <select
              className="input"
              value={excludeId}
              onChange={(e) => setExcludeId(e.target.value === '' ? '' : Number(e.target.value))}
              data-testid="newsletter-exclude"
            >
              <option value="">{t('form.excludeNone')}</option>
              {publishedNewsletters.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.subject}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="muted" style={{ marginBottom: 4 }}>{t('form.maxRecipientsLabel')}</div>
            <input
              type="number"
              min={1}
              className="input"
              value={maxRecipients}
              onChange={(e) => setMaxRecipients(e.target.value === '' ? '' : Number(e.target.value))}
              data-testid="newsletter-max-recipients"
            />
          </label>

          <div data-testid="newsletter-preview-count">
            {preview.isLoading && t('form.previewLoading')}
            {!preview.isLoading && preview.cappedCount !== undefined && (
              <strong>{t('form.previewCount', { capped: preview.cappedCount, total: preview.count })}</strong>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                  onClick={handleUpdate}
                  data-testid="newsletter-save-btn"
                >
                  {t('form.saveButton')}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={resetForm}
                  data-testid="newsletter-cancel-edit-btn"
                >
                  {t('form.cancelEditButton')}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canSubmit}
                onClick={handleCreate}
                data-testid="newsletter-create-btn"
              >
                {t('form.createButton')}
              </button>
            )}
          </div>
        </div>
      </div>

      {previewId !== null && (
        <div className="card" style={{ padding: 16, marginBottom: 24 }} data-testid="admin-newsletter-preview">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18 }}>{t('previewPane.title')}</h2>
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>{t('previewPane.hint')}</p>
            </div>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setPreviewId(null)}
              data-testid="newsletter-preview-close"
            >
              {t('previewPane.close')}
            </button>
          </div>

          {mailPreview.isLoading && <p className="muted">{t('previewPane.loading')}</p>}
          {mailPreview.isError && (
            <p role="alert" data-testid="newsletter-preview-error">
              {t('previewPane.error')}
            </p>
          )}
          {mailPreview.html && (
            <iframe
              title={t('previewPane.title')}
              srcDoc={mailPreview.html}
              sandbox=""
              style={{ width: '100%', height: '70vh', border: '1px solid var(--border, #ddd)', borderRadius: 8, background: '#fff' }}
              data-testid="newsletter-preview-frame"
            />
          )}
        </div>
      )}

      {isLoading && (
        <div aria-busy="true" data-testid="admin-newsletter-loading">
          {t('loading')}
        </div>
      )}

      {isError && (
        <div className="error-state" role="alert" data-testid="admin-newsletter-error">
          <p>{t('error.load')}</p>
          <button type="button" className="btn btn-primary" onClick={() => refetch()}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && (newsletters ?? []).length === 0 && (
        <div className="empty-state" data-testid="admin-newsletter-empty">
          <p>{t('empty.title')}</p>
        </div>
      )}

      {!isLoading && !isError && (newsletters ?? []).length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }} data-testid="admin-newsletter-list">
          <table className="table" aria-label={t('page.title')}>
            <thead>
              <tr>
                <th scope="col">{t('list.colSubject')}</th>
                <th scope="col">{t('list.colSegments')}</th>
                <th scope="col">{t('list.colRecipients')}</th>
                <th scope="col">{t('list.colPublishedAt')}</th>
                <th scope="col">{t('list.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {(newsletters ?? []).map((newsletter) => (
                <tr key={newsletter.id} data-testid={'admin-newsletter-row-' + newsletter.id}>
                  <td>{newsletter.subject}</td>
                  <td>
                    {newsletter.segments.map((segment) => (
                      <span key={segment} className="pill" style={{ marginRight: 4 }}>
                        {t('segments.' + segment)}
                      </span>
                    ))}
                  </td>
                  <td>{newsletter.recipient_count}</td>
                  <td>
                    {newsletter.published_at
                      ? formatDate(newsletter.published_at, i18next.language)
                      : t('list.notSentYet')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setPreviewId(newsletter.id)}
                        data-testid={'admin-newsletter-preview-' + newsletter.id}
                      >
                        {t('list.previewAction')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={testSend.isPending}
                        onClick={() => handleTestSend(newsletter)}
                        data-testid={'admin-newsletter-test-send-' + newsletter.id}
                      >
                        {t('list.testSendAction')}
                      </button>
                      {!newsletter.published_at && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => handleEdit(newsletter)}
                            data-testid={'admin-newsletter-edit-' + newsletter.id}
                          >
                            {t('list.editAction')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={publishNewsletter.isPending}
                            onClick={() => handlePublish(newsletter)}
                            data-testid={'admin-newsletter-publish-' + newsletter.id}
                          >
                            {t('list.publishAction')}
                          </button>
                        </>
                      )}
                    </div>
                    {publishError?.id === newsletter.id && (
                      <p className="muted" role="alert" style={{ marginTop: 4 }} data-testid={'admin-newsletter-publish-error-' + newsletter.id}>
                        {publishError.message}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
